import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiGet } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import { useDepartment } from '../contexts/DepartmentContext';
import { useVatMode } from '../hooks/useVatMode';
import { usePageTitle } from '../contexts/PageHeaderContext';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import { BTN } from '../constants/buttons';
import SharePdfButton from '../components/SharePdfButton';
import CompareViewBody from '../components/CompareViewBody';
import { buildBudgetVariancePdf } from '../utils/budgetVariancePdf';
import useBudgetInvalidation from '../hooks/useBudgetInvalidation';
import usePageViewLogger from '../hooks/usePageViewLogger';

/**
 * BudgetComparePopout — standalone /budget/compare page rendering the
 * same financial-statement variance view as the inline Compare drawer
 * (BudgetOverview.js). Reachable via the drawer's pop-out icon
 * (target="_blank") or by typing/sharing the URL.
 *
 * URL: /budget/compare?dept=<id>&version_a=<currentId>&version_b=<targetId>
 *
 * Use case: dual-monitor variance review. Buyer edits crew/cost-codes
 * on monitor A (/budget/manage-budgets), watches the variance on
 * monitor B (this page). Live updates flow via useBudgetInvalidation
 * (same dataBus topic the drawer + main report use).
 */
export default function BudgetComparePopout() {
  // 🚨 THIS PAGE LOGS ITS OWN PAGE VIEW, AND IT IS THE REASON THE HOOK IS NOT
  // "mounted once in Layout" ANY MORE (2026-09-16). `usePageViewLogger()` lives
  // in `components/Layout.js`, and this route is deliberately rendered OUTSIDE
  // the Layout wrapper (`App.js`, the chrome-less pop-out) — so it is an
  // AUTHENTICATED page that recorded nothing at all, on either Admin Analytics
  // panel. It is not in SKIP_PREFIXES either; it was simply invisible.
  // ⚠️ A route rendered outside `Layout` must either call this hook or be
  // declared in `App.pageViewCoverage.test.js`. That test fails CI otherwise.
  usePageViewLogger();
  const [searchParams] = useSearchParams();
  const { activeDepartment } = useDepartment();
  const { formatMoney } = useVatMode();

  const deptIdParam = searchParams.get('dept');
  const versionAParam = searchParams.get('version_a');
  const versionBParam = searchParams.get('version_b');

  const deptId = deptIdParam ? Number(deptIdParam) : null;
  const versionAId = versionAParam ? Number(versionAParam) : null;
  const versionBId = versionBParam ? Number(versionBParam) : null;

  const [report, setReport] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [costCodesExpanded, setCostCodesExpanded] = useState(true);
  const [labourExpanded, setLabourExpanded] = useState(true);
  const [unchangedExpanded, setUnchangedExpanded] = useState(false);

  // Dark mode — the pop-out renders outside Layout, so it owns its own
  // copy of the dark/light state. Initial value reads localStorage (the
  // same key Layout uses) so the pop-out opens in whatever mode the
  // main app was in. Falls back to the OS preference if the user has
  // never set it explicitly.
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem('sfx_dark_mode');
      if (stored !== null) return stored === 'true';
    } catch (_e) { /* ignore SSR / private-mode */ }
    return typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply the `dark` class to <html> + persist whenever state flips.
  // Same key Layout writes to, so toggling here is reflected in the
  // main app on next open (and vice versa via the storage listener
  // below).
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    try { localStorage.setItem('sfx_dark_mode', String(darkMode)); } catch (_e) {}
  }, [darkMode]);

  // Cross-tab live sync — if the user toggles dark mode in the main app
  // tab (Layout), the pop-out tab follows. Native `storage` event fires
  // in OTHER tabs when localStorage changes; in-tab toggles use the
  // effect above instead.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'sfx_dark_mode' && e.newValue != null) {
        setDarkMode(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Resolve version names from the report payload. Falls back to the
  // raw id if the report hasn't loaded yet.
  const currentVersionName = useMemo(() => {
    const v = report?.versions?.find((x) => x.id === versionAId);
    return v?.version_name || (versionAId ? `v${versionAId}` : 'Current');
  }, [report, versionAId]);
  const comparisonVersionName = useMemo(() => {
    const v = report?.versions?.find((x) => x.id === versionBId);
    return v?.version_name || (versionBId ? `v${versionBId}` : 'Comparison');
  }, [report, versionBId]);

  // Browser tab title — makes multiple comparison tabs distinguishable.
  usePageTitle(`Variance — ${currentVersionName} vs ${comparisonVersionName}`);

  // Validate URL params before fetching. Missing or non-numeric values
  // shouldn't fire a malformed API call.
  const paramsValid = !!(deptId && versionAId && versionBId && versionAId !== versionBId);

  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!paramsValid) {
      setError('Comparison URL is missing dept / version_a / version_b parameters.');
      setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const [reportRes, cmpRes] = await Promise.all([
        apiGet(`/api/budget/report?department_id=${deptId}`),
        apiGet(`/api/budget/versions/compare?dept_id=${deptId}&version_a=${versionAId}&version_b=${versionBId}`),
      ]);
      if (!reportRes.ok) {
        const body = await reportRes.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to load budget report');
      }
      if (!cmpRes.ok) {
        const body = await cmpRes.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to load comparison');
      }
      const [reportBody, cmpBody] = await Promise.all([reportRes.json(), cmpRes.json()]);
      setReport(reportBody);
      setCompareData(cmpBody);
      if (silent) setError(null);
    } catch (err) {
      // Silent refresh: keep the prior data on transient failures rather
      // than blanking the page.
      if (!silent) setError(friendlyErrorMessage(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [paramsValid, deptId, versionAId, versionBId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Cross-tab live update + tab-visibility re-fetch. Same dataBus topic
  // the drawer + main report use, so any cost-code / crew-member edit
  // anywhere in the app propagates here within ~50ms.
  const silentRefetch = useCallback(() => fetchAll({ silent: true }), [fetchAll]);
  useBudgetInvalidation(deptId, silentRefetch);

  // Resolve dept/production strings for the header + PDF.
  const departmentName = activeDepartment?.department_name
    || report?.versions?.[0]?.department_name
    || 'Department';
  const productionName = activeDepartment?.production_name || report?.production_name || '';
  const companyName = activeDepartment?.company_name || '';

  // Build the JSX bits we inject into CompareViewBody's header band as
  // slot props. The standalone amber top bar is gone — everything that
  // identifies this page (caption + dark toggle + Download/Print) now
  // lives inside the header band itself.
  // Rendered INSIDE the <h2> as a trailing sub-caption tail — must be a
  // span (block elements like <p> would be invalid HTML inside h2). Lighter
  // weight + smaller size so it reads as helper text after the title proper.
  const captionSlot = (
    <span className="font-normal text-xs text-amber-800 dark:text-amber-300">
      Ad-hoc comparison between two budget versions. For the full Status Report (cost breakdown + variance vs baseline), use{' '}
      <Link
        to={`/budget/overview${deptId ? `?dept=${deptId}` : ''}`}
        className="underline hover:text-amber-700 dark:hover:text-amber-100"
      >
        Budget Overview's Download / Print
      </Link>.
    </span>
  );

  const darkModeToggle = (
    <button
      type="button"
      onClick={() => setDarkMode((prev) => !prev)}
      className="p-1.5 rounded text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
      title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      aria-label={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
    >
      {darkMode ? (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );

  const downloadButton = (compareData && report) ? (
    <SharePdfButton
      renderPdfMakeDoc={() => buildBudgetVariancePdf({
        includeOverview: false,
        includeVariance: true,
        includeUnchanged: unchangedExpanded,
        reportData: report,
        departmentName,
        productionName,
        companyName,
        /* 🔻 `includeVat` NO LONGER PASSED (2026-08-08) — see the twin call in
           `BudgetOverview.js`. Omitting it makes `utils/variancePdf.js` drop the
           "· ex. VAT" / "· incl. VAT" subtitle suffix entirely; passing `false`
           would print a claim the app can no longer make. */
        formatMoney,
        versionName: currentVersionName,
        compareData,
        currentVersionName,
        comparisonVersionName,
        baselineVersionId: versionBId,
        // The user-picked column isn't necessarily the real baseline,
        // so tag it honestly as "Selected" — distinct from the
        // Overview PDF where "(Baseline)" IS accurate.
        baselineLabel: 'Selected',
        pdfTitle: `Version Comparison Report - Current (${currentVersionName}) Vs (${comparisonVersionName})`,
      })}
      filename={`${departmentName}-Variance-${currentVersionName}-vs-${comparisonVersionName}.pdf`}
      title={`${departmentName} Version Comparison Report`}
      orientation="landscape"
      dropDirection="down"
      className={BTN.secondary}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      Download / Print
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </SharePdfButton>
  ) : null;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Loading / error / content */}
      {loading && (
        <div className="p-6">
          <LoadingSpinner message="Loading comparison..." />
        </div>
      )}
      {error && !loading && (
        <div className="m-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}
      {!loading && !error && compareData && (
        <CompareViewBody
          compareData={compareData}
          currentVersionName={currentVersionName}
          comparisonVersionName={comparisonVersionName}
          productionName={productionName}
          departmentName={departmentName}
          companyName={companyName}
          formatMoney={formatMoney}
          formatDateShort={formatDateShort}
          costCodesExpanded={costCodesExpanded}
          onToggleCostCodes={() => setCostCodesExpanded((p) => !p)}
          labourExpanded={labourExpanded}
          onToggleLabour={() => setLabourExpanded((p) => !p)}
          unchangedExpanded={unchangedExpanded}
          onToggleUnchanged={() => setUnchangedExpanded((p) => !p)}
          headerExtraLeft={captionSlot}
          headerToolsBeforeTotals={darkModeToggle}
          headerToolsAfterTotals={downloadButton}
        />
      )}
    </div>
  );
}
